/*  eslint-disable react-hooks/exhaustive-deps  */

import React, { useEffect, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import {
  I2cInput,
  I2cMenuItem,
  I2cSelect,
  I2cDatePicker,
} from "@webcomponents/i2cwebcomponents/dist/react";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import UserManagmentService from "../../../Services/UsermanagmentService";
import "../Prepaid.scss";
import timezone from "./timezones.json";
/**
 * Component for capturing and validating project details.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setFormValidStates - Function to set form validation states.
 * @returns {React.Component} - The rendered component.
 */
const ProjectDetails = ({
  type,
  setFormValidStates,
  setAddFormData,
  addFormData,
  formDataJson,
  isFetching,
}) => {
  // Helper function to parse stored JSON values
  const parseUserJson = (jsonString) => {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  };

  // Define validation schema using Zod
  const validationSchema = z
    .object({
      pd_client_name: z.string().min(1, { message: "Client Name is required" }),
      pd_region: z.string().min(1, { message: "Region is required" }),
      pd_pid: z
        .string()
        .optional(),
      pd_team_avail: z.string().optional(),
      pd_ticket_num: z.string().optional(),
      pd_kick_off: z.string().optional(),
      pd_go_live: z.string().optional(),
      pd_pmo: z.string().optional(),
      pd_sales_rep: z.string().optional(),
      // pd_solution_architect: z
      //   .string()
      //   .min(1, { message: "Solution Architect is required" }),
      pd_solution_architect: z.string().optional(), 
      pd_po_resource: z.string().optional(), 
      pd_scrum_master: z.string().optional(), 
      pd_account_manager: z.string().optional(),
      pd_sales_engineer: z.string().optional(),
    })
  // .refine(
  //   (data) => {
  //     const kickOffDate = new Date(data.pd_kick_off);
  //     const goLiveDate = new Date(data.pd_go_live);
  //     if (isNaN(kickOffDate.getDate())) return true;
  //     return goLiveDate > kickOffDate;
  //   },
  //   {
  //     message: "Go-Live date should be greater than Kick off date",
  //     path: ["pd_go_live"],
  //   },
  // );
  // .refine(
  //   (data) => {
  //     const kickOffDate = new Date(data.pd_kick_off);
  //     const goLiveDate = new Date(data.pd_go_live);
  //     return goLiveDate < kickOffDate;
  //   },
  //   {
  //     message: "Kick off date should be less than Go Live date",
  //     path: ["pd_kick_off"],
  //   },
  // );

  // Initialize React Hook Form with Zod resolver
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      pd_client_name: "",
      pd_region: "",
      pd_team_avail: "",
      pd_pid: "",
      pd_ticket_num: "",
      pd_kick_off: "",
      pd_go_live: "",
      pd_sales_rep: "",
      pd_pmo: "",
      pd_solution_architect: "",
      pd_po_resource: "",
      pd_scrum_master: "",
      pd_account_manager: "",
      pd_sales_engineer: "",
    },
  });

  const fields = [
    "pd_client_name",
    "pd_region",
    "pd_team_avail",
    "pd_pid",
    "pd_ticket_num",
    "pd_kick_off",
    "pd_go_live",
    "pd_sales_rep",
    "pd_pmo",
    "pd_solution_architect",
    "pd_po_resource",
    "pd_scrum_master",
    "pd_account_manager",
    "pd_sales_engineer",
  ];
  const allRegions = [
    "Africa",
    "Central America",
    "Middle East",
    "Pacific",
    "Asia",
    "Europe",
    "North America",
    "South America",
  ];

  // State for Azure AD users - separate for each role type
  const [projectManagers, setProjectManagers] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [poResources, setPOResources] = useState([]);
  const [scrumMasters, setScrumMasters] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  const [salesEngineers, setSalesEngineers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [usersFetchError, setUsersFetchError] = useState(null);

  // Fetch Azure AD users on component mount
  useEffect(() => {
    const fetchWithRetry = async (fn, retries = 3, delayMs = 1500) => {
      let lastError;
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          lastError = err;
          if (attempt < retries - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
          }
        }
      }
      throw lastError;
    };

    const fetchAzureUsers = async () => {
      try {
        setLoadingUsers(true);

        // Use allSettled so a single endpoint failure doesn't block the rest.
        // Each call is individually retried up to 3 times with incremental back-off.
        const [managersResult, architectsResult, poResult, scrumResult, accountManagersResult, salesEngineersResult] =
          await Promise.allSettled([
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "manager,Associate Project Manager,Senior,Principal", "")),
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "architect, Business Analyst", "")),
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "", "Product Operations")),
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "scrum master,coordinator,Jr. Project Manager", "")),
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "account", "")),
            fetchWithRetry(() => UserManagmentService.getAzureUsers("", "sales", "")),
          ]);

        if (managersResult.status === "fulfilled" && managersResult.value.data.status) {
          setProjectManagers(managersResult.value.data.data ?? []);
        }
        if (architectsResult.status === "fulfilled" && architectsResult.value.data.status) {
          setArchitects(architectsResult.value.data.data ?? []);
        }
        if (poResult.status === "fulfilled" && poResult.value.data.status) {
          setPOResources(poResult.value.data.data ?? []);
        }
        if (scrumResult.status === "fulfilled" && scrumResult.value.data.status) {
          setScrumMasters(scrumResult.value.data.data ?? []);
        }
        if (accountManagersResult.status === "fulfilled" && accountManagersResult.value.data.status) {
          setAccountManagers(accountManagersResult.value.data.data ?? []);
        }
        if (salesEngineersResult.status === "fulfilled" && salesEngineersResult.value.data.status) {
          setSalesEngineers(salesEngineersResult.value.data.data ?? []);
        }

        const anyFailed = [managersResult, architectsResult, poResult, scrumResult, accountManagersResult, salesEngineersResult]
          .some((r) => r.status === "rejected");

        if (anyFailed) {
          const firstRejected = [managersResult, architectsResult, poResult, scrumResult, accountManagersResult, salesEngineersResult]
            .find((r) => r.status === "rejected");
          const err = firstRejected?.reason;
          console.error("[ProjectDetails] One or more Azure AD requests failed after retries:", err);
          setUsersFetchError(
            err?.response?.data?.message || err?.message || "Some user lists could not be loaded"
          );
        } else {
          setUsersFetchError(null);
        }
      } catch (error) {
        console.error("[ProjectDetails] Unexpected error fetching Azure users:", error);
        setUsersFetchError(error?.message || "Error loading users from Azure AD");
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAzureUsers();
  }, []);

  useEffect(() => {
    if (!isFetching && !loadingUsers && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[0]["0_0"][field] ?? "";
        methods.setValue(field, value);
      });
      methods.trigger();
    }
  }, [isFetching, loadingUsers]);

  const {
    control,
    formState: { errors },
  } = methods;

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    const updatedData = {
      ...addFormData,
      0: {
        ...addFormData["0"],
        "0_0": { ...values },
      },
    };
    setAddFormData(updatedData);
  };
  // Updsate form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("0_0", true);
    else setFormValidStates("0_0", false);
  }, [Object.keys(errors).length]);

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-column-sm gap-row-md" className="section-space">
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_client_name"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    required
                    placeholder="Enter Client Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Client Name"
                    name={"pd_client_name"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.pd_client_name &&
                  methods.formState.touchedFields.pd_client_name
                    ? true
                    : false
                }
                message={errors.pd_client_name?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_region"
                control={control}
                render={({ field }) => (
                  <I2cSelect
                    {...field}
                    name="pd_region"
                    onI2cChange={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    label="Region"
                    required
                    placeholder="Enter Region"
                    size="large"
                    search 
                  >
                    {allRegions.map((regions, index) => (
                      <I2cMenuItem key={index + regions} value={regions}>
                        {regions}
                      </I2cMenuItem>
                    ))}
                  </I2cSelect>
                )}
              /> 
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_team_avail"
                control={control}
                render={({ field }) => (
                  <I2cSelect
                    {...field}
                    label="Project Team Availability"
                    onI2cChange={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    placeholder="Enter Time zone"
                    name="pd_team_avail"
                    size="large"
                    search
                    clearable
                  >
                    {timezone.map((timezoneList, index) => (
                      <I2cMenuItem key={index} value={timezoneList.value}>
                        {timezoneList.value}
                      </I2cMenuItem>
                    ))}
                  </I2cSelect>
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_team_avail &&
                    methods.formState.touchedFields.pd_team_avail
                    ? true
                    : false
                }
                message={errors.pd_team_avail?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_pid"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Project ID"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    placeholder="Enter Project ID"
                    name="pd_pid"
                    size="large"
                  ></I2cInput>
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_pid && methods.formState.touchedFields.pd_pid
                    ? true
                    : false
                }
                message={errors.pd_pid?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_ticket_num"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    label="Ticket Number"
                    onI2cInput={(e) => {
                      field.onChange(e.target.value);
                      handleValueChanges();
                    }}
                    placeholder="Enter Ticket Number"
                    name="pd_ticket_num"
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_ticket_num &&
                    methods.formState.touchedFields.pd_ticket_num
                    ? true
                    : false
                }
                message={errors.pd_ticket_num?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_po_resource"
                control={control}
                render={({ field }) => {
                  const selectedUser = parseUserJson(field.value);

                  return (
                    <I2cSelect
                      {...field}
                      label="PO Resource"
                      hoist
                      onI2cChange={(e) => {
                        const selectedEmail = e.target.value;
                        const user = poResources.find(u => u.email === selectedEmail);

                        if (user) {
                          // Store as JSON string with name, email, and title
                          const userJson = JSON.stringify({
                            name: user.name,
                            email: user.email || '',
                            title: user.title || ''
                          });
                          field.onChange(userJson);
                        } else {
                          field.onChange('');
                        }
                        handleValueChanges();
                      }}
                      placeholder={loadingUsers ? "Loading users..." : "Select PO Resource"}
                      name="pd_po_resource"
                      size="large"
                      search
                       clearable
                      disabled={loadingUsers}
                      value={selectedUser ? selectedUser.email : ''}
                    >
                      {usersFetchError ? (
                        <I2cMenuItem value="" disabled>
                          {usersFetchError} - Please type manually
                        </I2cMenuItem>
                      ) : (
                        poResources.map((user, index) => (
                          <I2cMenuItem key={user.email || index} value={user.email}>
                            {user.name}
                          </I2cMenuItem>
                        ))
                      )}
                    </I2cSelect>
                  );
                }}
              />
              <ErrorMessage
                show={
                  errors.pd_po_resource &&
                    methods.formState.touchedFields.pd_po_resource
                    ? true
                    : false
                }
                message={errors.pd_po_resource?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_scrum_master"
                control={control}
                render={({ field }) => {
                  const selectedUser = parseUserJson(field.value);

                  return (
                    <I2cSelect
                      {...field}
                      label="Scrum Master"
                      hoist
                      onI2cChange={(e) => {
                        const selectedEmail = e.target.value;
                        const user = scrumMasters.find(u => u.email === selectedEmail);

                        if (user) {
                          // Store as JSON string with name, email, and title
                          const userJson = JSON.stringify({
                            name: user.name,
                            email: user.email || '',
                            title: user.title || ''
                          });
                          field.onChange(userJson);
                        } else {
                          field.onChange('');
                        }
                        handleValueChanges();
                      }}
                      placeholder={loadingUsers ? "Loading users..." : "Select Scrum Master"}
                      name="pd_scrum_master"
                      size="large"
                      search
                       clearable
                      disabled={loadingUsers}
                      value={selectedUser ? selectedUser.email : ''}
                    >
                      {usersFetchError ? (
                        <I2cMenuItem value="" disabled>
                          {usersFetchError} - Please type manually
                        </I2cMenuItem>
                      ) : (
                        scrumMasters.map((user, index) => (
                          <I2cMenuItem key={user.email || index} value={user.email}>
                            {user.name}
                          </I2cMenuItem>
                        ))
                      )}
                    </I2cSelect>
                  );
                }}
              />
              <ErrorMessage
                show={
                  errors.pd_scrum_master &&
                    methods.formState.touchedFields.pd_scrum_master
                    ? true
                    : false
                }
                message={errors.pd_scrum_master?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_kick_off"
                control={control}
                render={({ field }) => (
                  <I2cDatePicker
                    {...field}
                    I2cDatePicker
                    errorMessageInvisible={true}
                    suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                    hoist
                    dateFormat="F d, Y"
                    rotateIcon
                    onI2cDateApply={(e) => {
                      if (e.target?.appliedDate) {
                        const kickOffDate =
                          e.target?.appliedDate[0]?.toDateString() ?? "";
                        const goLiveValue = methods.getValues("pd_go_live");
                        const goLiveDate = new Date(goLiveValue);
                        if (goLiveValue && goLiveDate < new Date(kickOffDate)) {
                          methods.setError("pd_kick_off", {
                            message:
                              "Kick off date should be less than the go live date",
                          });
                        }
                        field.onChange(kickOffDate);
                      } else field.onChange("");
                      handleValueChanges();
                      methods.trigger();
                    }}
                    placeholder="Enter Project Kick Off Date"
                    // minDate={new Date().setHours(0, 0, 0, 0)}
                    size="large"
                    name="pd_kick_off"
                    label="Project Kick-off Date"
                    defaultDate={
                      field.value
                        ? new Date(field.value)
                        : ""
                    }
                    
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.pd_kick_off &&
                    methods.formState.touchedFields.pd_kick_off
                    ? true
                    : false
                }
                message={errors.pd_kick_off?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="pd_go_live"
                control={control}
                suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                render={({ field }) => {
                  // Calculate minDate based on kick-off date without using watch
                  const kickOffValue = methods.getValues("pd_kick_off");
                  const kickOffDate = kickOffValue ? new Date(kickOffValue) : null;
                  const minDate = kickOffDate && !isNaN(kickOffDate.getDate())
                    ? new Date(
                      kickOffDate.getFullYear(),
                      kickOffDate.getMonth(),
                      kickOffDate.getDate() + 1,
                      0,
                      0,
                      0,
                    )
                    : new Date(
                      new Date().getFullYear(),
                      new Date().getMonth(),
                      new Date().getDate() + 1,
                      0,
                      0,
                      0,
                    );

                  return (
                    <I2cDatePicker
                      {...field}
                      name="pd_go_live"
                      I2cDatePicker
                      errorMessageInvisible={true}
                      rotateIcon
                      placeholder="Enter Project Go-Live date"
                      suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                      hoist
                      minDate={minDate}
                      onI2cDateApply={(e) => {
                        if (e.target?.appliedDate)
                          field.onChange(
                            e.target?.appliedDate[0]?.toDateString() ?? "",
                          );
                        else field.onChange("");
                        handleValueChanges();
                      }}
                      dateFormat="F d, Y"
                      size="large"
                      label="Go-live Date (Projected)"
                      defaultDate={
                        field.value
                          ? new Date(field.value)
                          : ""
                      }
                    />
                  );
                }}
              />
              <ErrorMessage
                show={
                  errors.pd_go_live &&
                    methods.formState.touchedFields.pd_go_live
                    ? true
                    : false
                }
                message={errors.pd_go_live?.message ?? "Invalid input"}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="sub-heading mb-16">i2c Contact Details</div>
          <div i2c-grid="grid gap-column-sm gap-row-md">
            <div i2c-grid="4@md">
              <div className="box-wrap multiple">
                <div className="box">
                  <div className="single-field-wrapper">
                    <Controller
                      name="pd_sales_rep"
                      control={control}
                      render={({ field }) => (
                        <I2cInput
                          {...field}
                          label="i2c Inc. Sales Representative"
                          onI2cInput={(e) => {
                            field.onChange(e.target.value);
                            handleValueChanges();
                          }}
                          type="text"
                          placeholder="Enter i2c Inc. Sales Rep"
                          name="pd_sales_rep"
                          size="large"
                        />
                      )}
                    />
                    <ErrorMessage
                      show={
                        errors.pd_sales_rep &&
                          methods.formState.touchedFields.pd_sales_rep
                          ? true
                          : false
                      }
                      message={errors.pd_sales_rep?.message ?? "Invalid input"}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div i2c-grid="4@md">
              <div className="box-wrap multiple">
                <div className="box">
                  <div className="single-field-wrapper">
                    <Controller
                      name="pd_pmo"
                      control={control}
                      render={({ field }) => {
                        const selectedUser = parseUserJson(field.value);

                        return (
                          <I2cSelect
                            {...field}
                            label="i2c Inc. Project Manager"
                            hoist
                            onI2cChange={(e) => {
                              const selectedEmail = e.target.value;
                              const user = projectManagers.find(u => u.email === selectedEmail);

                              if (user) {
                                // Store as JSON string with name, email, and title
                                const userJson = JSON.stringify({
                                  name: user.name,
                                  email: user.email || '',
                                  title: user.title || ''
                                });
                                field.onChange(userJson);
                              } else {
                                field.onChange('');
                              }
                              handleValueChanges();
                            }}
                            placeholder={loadingUsers ? "Loading users..." : "Select Project Manager"}
                            name="pd_pmo"
                            size="large"
                            search
                            clearable
                            disabled={loadingUsers}
                            value={selectedUser ? selectedUser.email : ''}
                          >
                            {usersFetchError ? (
                              <I2cMenuItem value="" disabled>
                                {usersFetchError} - Please type manually
                              </I2cMenuItem>
                            ) : (
                              projectManagers.map((user, index) => (
                                <I2cMenuItem key={user.email || index} value={user.email}>
                                  {user.name}
                                </I2cMenuItem>
                              ))
                            )}
                          </I2cSelect>
                        );
                      }}
                    />

                  </div>
                </div>
              </div>
            </div>
            <div i2c-grid="4@md">
              <div className="box-wrap multiple">
                <div className="box">
                  <div className="single-field-wrapper">
                    <Controller
                      name="pd_solution_architect"
                      control={control}
                      render={({ field }) => {
                        const selectedUser = parseUserJson(field.value);

                        return (
                          <I2cSelect
                            {...field}
                            label="i2c Inc. Solution Architect"
                            hoist
                            onI2cChange={(e) => {
                              const selectedEmail = e.target.value;
                              const user = architects.find(u => u.email === selectedEmail);

                              if (user) {
                                // Store as JSON string with name, email, and title
                                const userJson = JSON.stringify({
                                  name: user.name,
                                  email: user.email || '',
                                  title: user.title || ''
                                });
                                field.onChange(userJson);
                              } else {
                                field.onChange('');
                              }
                              handleValueChanges();
                            }}
                            placeholder={loadingUsers ? "Loading users..." : "Select Solution Architect"}
                            name="pd_solution_architect"
                            size="large"
                            search
                            clearable
                            disabled={loadingUsers}
                            value={selectedUser ? selectedUser.email : ''}
                          >
                            {usersFetchError ? (
                              <I2cMenuItem value="" disabled>
                                {usersFetchError} - Please type manually
                              </I2cMenuItem>
                            ) : (
                              architects.map((user, index) => (
                                <I2cMenuItem key={user.email || index} value={user.email}>
                                  {user.name}
                                </I2cMenuItem>
                              ))
                            )}
                          </I2cSelect>
                        );
                      }}
                    />

                  </div>
                </div>
              </div>
            </div>
            <div i2c-grid="4@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pd_account_manager"
                  control={control}
                  render={({ field }) => {
                    const selectedUser = parseUserJson(field.value);
                    return (
                      <I2cSelect
                        {...field}
                        label="i2c Account Manager"
                        hoist
                        onI2cChange={(e) => {
                          const selectedEmail = e.target.value;
                          const user = accountManagers.find(u => u.email === selectedEmail);
                          if (user) {
                            field.onChange(JSON.stringify({ name: user.name, email: user.email || '', title: user.title || '' }));
                          } else {
                            field.onChange('');
                          }
                          handleValueChanges();
                        }}
                        placeholder={loadingUsers ? "Loading users..." : "Select Account Manager"}
                        name="pd_account_manager"
                        size="large"
                        search
                        clearable
                        disabled={loadingUsers}
                        value={selectedUser ? selectedUser.email : ''}
                      >
                        {usersFetchError ? (
                          <I2cMenuItem value="" disabled>
                            {usersFetchError} - Please type manually
                          </I2cMenuItem>
                        ) : (
                          accountManagers.map((user, index) => (
                            <I2cMenuItem key={user.email || index} value={user.email}>
                              {user.name}
                            </I2cMenuItem>
                          ))
                        )}
                      </I2cSelect>
                    );
                  }}
                />
                <ErrorMessage
                  show={
                    errors.pd_account_manager &&
                      methods.formState.touchedFields.pd_account_manager
                      ? true
                      : false
                  }
                  message={
                    errors.pd_account_manager?.message ?? "Invalid input"
                  }
                />
              </div>
            </div>
            <div i2c-grid="4@md">
              <div className="single-field-wrapper">
                <Controller
                  name="pd_sales_engineer"
                  control={control}
                  render={({ field }) => {
                    const selectedUser = parseUserJson(field.value);
                    return (
                      <I2cSelect
                        {...field}
                        label="i2c Sales Engineer"
                        hoist
                        onI2cChange={(e) => {
                          const selectedEmail = e.target.value;
                          const user = salesEngineers.find(u => u.email === selectedEmail);
                          if (user) {
                            field.onChange(JSON.stringify({ name: user.name, email: user.email || '', title: user.title || '' }));
                          } else {
                            field.onChange('');
                          }
                          handleValueChanges();
                        }}
                        placeholder={loadingUsers ? "Loading users..." : "Select Sales Engineer"}
                        name="pd_sales_engineer"
                        size="large"
                        search
                        clearable
                        disabled={loadingUsers}
                        value={selectedUser ? selectedUser.email : ''}
                      >
                        {usersFetchError ? (
                          <I2cMenuItem value="" disabled>
                            {usersFetchError} - Please type manually
                          </I2cMenuItem>
                        ) : (
                          salesEngineers.map((user, index) => (
                            <I2cMenuItem key={user.email || index} value={user.email}>
                              {user.name}
                            </I2cMenuItem>
                          ))
                        )}
                      </I2cSelect>
                    );
                  }}
                />
                <ErrorMessage
                  show={
                    errors.pd_sales_engineer &&
                      methods.formState.touchedFields.pd_sales_engineer
                      ? true
                      : false
                  }
                  message={errors.pd_sales_engineer?.message ?? "Invalid input"}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProjectDetails;
